import { formatMoney } from '@expenditure/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '../lib/api';
import { haptic, saveImage } from '../lib/telegram';
import type { KhqrForPayment } from '../lib/types';
import { Icon } from './Icon';
import { QrCode } from './QrCode';
import { useToast } from './Toast';

/** The KHQR "card" (name, amount, code) plus "Save QR image" for paying from a bank app. */
export function KhqrCard({ khqr }: { khqr: Pick<KhqrForPayment, 'payload' | 'name' | 'amountMinor' | 'currency' | 'imageUrl'> }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const amount = formatMoney(khqr.amountMinor, khqr.currency).replace(/[^\d.,]/g, '');
      const result = await saveImage(apiUrl(khqr.imageUrl), `KHQR-${khqr.name.replace(/\W+/g, '-')}-${amount}.png`);
      if (result === 'saved') {
        haptic.success();
        toast(t('settle.qrSaved'));
      }
    } catch {
      toast(t('errors.generic'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="khqr-card">
        <div className="khqr-card-header">KHQR</div>
        <div className="khqr-card-body">
          <div className="small muted">{khqr.name}</div>
          <div className="num" style={{ fontSize: 24, fontWeight: 800 }}>
            {formatMoney(khqr.amountMinor, khqr.currency)}
          </div>
          <QrCode value={khqr.payload} size={220} label={khqr.name} />
        </div>
      </div>
      <button type="button" className="btn btn--soft btn--block" disabled={saving} onClick={() => void save()}>
        <Icon name={saving ? 'loader' : 'download'} size={18} className={saving ? 'spin' : undefined} />
        {t('settle.saveQr')}
      </button>
      <div className="muted small">{t('settle.saveQrHint')}</div>
    </>
  );
}
